import { Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ToastService } from '../../../services/toast.service';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../../services/auth.service';
import { RoleService } from '../../../services/role.service';
import { LoggerService } from '../../../services/logger.service';
import { LocationService } from '../../../services/location.service';
import { PackageService } from '../../../services/package.service';
import { EmployeeService } from '../../../services/employee.service';
import { UserRole } from '../../../shared/types/role.interface';
import { formatDateTimeInTimeZone, getUserTimezone } from '../../../shared/utils/date.util';
import {
  ImpersonateAdminApiData,
  ImpersonationSessionPayload,
  ImpersonationTargetType,
} from '../models/impersonation.models';
import {
  IMPERSONATION_USER_DATA_KEY,
  IMPERSONATION_TUNNEL_DATA_KEY,
  clearActiveImpersonationSessions,
  clearImpersonationHandoff,
  consumeImpersonationHandoffIntoStorage,
  readActiveImpersonationTunnelSession,
  readActiveImpersonationUserSession,
  removeImpersonationStorageItem,
  setImpersonationStorageItem,
  writeImpersonationHandoff,
} from '../../../shared/utils/impersonation-handoff.util';
import { getDefaultImpersonationRoute } from '../utils/impersonation-permissions.util';

/** Auth / public routes where the impersonation banner must never show. */
const BANNER_HIDDEN_EXACT_PATHS = new Set([
  '/login',
  '/tunnel/login',
  '/forgot-password',
  '/reset-password',
  '/resend-email',
  '/public-stream',
]);

@Injectable({
  providedIn: 'root',
})
export class ImpersonationService {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly roleService = inject(RoleService);
  private readonly logger = inject(LoggerService);
  private readonly messageService = inject(ToastService);
  private readonly locationService = inject(LocationService);
  private readonly packageService = inject(PackageService);
  private readonly employeeService = inject(EmployeeService);

  readonly isImpersonating = signal(false);
  readonly bannerTargetLabel = signal('');
  readonly bannerActorLabel = signal('');
  readonly bannerExpiresAt = signal('');

  constructor() {
    this.refreshFromStorage();
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.refreshFromStorage());
  }

  refreshFromStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }
    // Ensure new-tab handoff is installed before reading the session.
    consumeImpersonationHandoffIntoStorage();

    // Only show banner while actively viewing an impersonated app surface.
    if (this.shouldHideBanner(window.location.pathname)) {
      this.clearBanner();
      return;
    }

    const user = readActiveImpersonationUserSession();
    if (user?.['impersonated']) {
      this.applyBannerFromSession(user);
      return;
    }
    const tunnel = readActiveImpersonationTunnelSession();
    if (tunnel?.['impersonated']) {
      this.applyBannerFromSession(tunnel);
      return;
    }
    this.clearBanner();
  }

  private shouldHideBanner(pathname: string): boolean {
    const path = (pathname || '').split('?')[0] || '';
    if (path.startsWith('/admin/') || BANNER_HIDDEN_EXACT_PATHS.has(path)) {
      return true;
    }
    return false;
  }

  startFromAdminImpersonateApi(
    data: ImpersonateAdminApiData,
    extras?: { company_id?: string; company_name?: string; timezone?: string }
  ): boolean {
    return this.startFromImpersonateApi(data, 'admin', extras);
  }

  startFromStaffImpersonateApi(
    data: ImpersonateAdminApiData,
    extras?: { company_id?: string; company_name?: string; timezone?: string }
  ): boolean {
    return this.startFromImpersonateApi(data, 'attendant', extras);
  }

  startFromTunnelImpersonateApi(
    data: ImpersonateAdminApiData,
    extras?: {
      company_id?: string;
      company_name?: string;
      timezone?: string;
      location_id?: string;
      branch?: string;
      name?: string;
      email?: string;
    }
  ): boolean {
    const parsed = this.parseImpersonateApiToken(data);
    if (!parsed) {
      return false;
    }

    const { token, user, imp } = parsed;
    const locationId =
      extras?.location_id ||
      (user.location_id != null
        ? String(user.location_id)
        : user.location != null
          ? String(user.location)
          : undefined);

    this.startImpersonation(
      {
        ...this.buildBaseImpersonationPayload(token, user, imp, extras),
        target_type: 'tunnel',
        email:
          user.email != null
            ? String(user.email)
            : extras?.email != null
              ? extras.email
              : undefined,
        name:
          user.name != null ? String(user.name) : extras?.name != null ? extras.name : undefined,
        role: UserRole.TUNNEL,
        location: locationId,
        location_id: locationId,
        branch: extras?.branch || (user['branch'] != null ? String(user['branch']) : undefined),
      },
      { openInNewTab: true }
    );
    return true;
  }

  private startFromImpersonateApi(
    data: ImpersonateAdminApiData,
    targetType: 'admin' | 'attendant' | 'attendant_plus' | 'staff',
    extras?: { company_id?: string; company_name?: string; timezone?: string }
  ): boolean {
    const parsed = this.parseImpersonateApiToken(data);
    if (!parsed) {
      return false;
    }

    const { token, user, imp } = parsed;
    const roleRaw = String(user.role || targetType).toLowerCase();
    let role: UserRole = UserRole.ADMIN;
    if (roleRaw === UserRole.STAFF || targetType === 'staff') {
      role = UserRole.STAFF;
    } else if (
      targetType === 'attendant' ||
      targetType === 'attendant_plus' ||
      roleRaw === UserRole.ATTENDANT ||
      roleRaw === UserRole.ATTENDANT_PLUS
    ) {
      role = roleRaw === UserRole.ATTENDANT_PLUS ? UserRole.ATTENDANT_PLUS : UserRole.ATTENDANT;
    }
    const locationId =
      user.location_id != null
        ? String(user.location_id)
        : user.location != null
          ? String(user.location)
          : undefined;

    const sessionTargetType: ImpersonationTargetType =
      role === UserRole.STAFF
        ? 'staff'
        : role === UserRole.ATTENDANT_PLUS
          ? 'attendant_plus'
          : role === UserRole.ATTENDANT
            ? 'attendant'
            : 'admin';

    this.startImpersonation(
      {
        ...this.buildBaseImpersonationPayload(token, user, imp, extras),
        target_type: sessionTargetType,
        email: user.email != null ? String(user.email) : undefined,
        name: user.name != null ? String(user.name) : undefined,
        role,
        location: locationId,
        location_id: locationId,
      },
      { openInNewTab: true }
    );
    return true;
  }

  private parseImpersonateApiToken(data: ImpersonateAdminApiData): {
    token: string;
    user: NonNullable<ImpersonateAdminApiData['user']>;
    imp: NonNullable<ImpersonateAdminApiData['impersonation']>;
  } | null {
    const token = String(data.token || data.access_token || '').trim();
    if (!token) {
      return null;
    }
    const locationPayload = data['location'];
    return {
      token,
      user: {
        ...(typeof locationPayload === 'object' && locationPayload ? locationPayload : {}),
        ...(data.user || {}),
      },
      imp: data.impersonation || {},
    };
  }

  private buildBaseImpersonationPayload(
    token: string,
    user: NonNullable<ImpersonateAdminApiData['user']>,
    imp: NonNullable<ImpersonateAdminApiData['impersonation']>,
    extras?: { company_id?: string; company_name?: string; timezone?: string }
  ): Pick<
    ImpersonationSessionPayload,
    | 'token'
    | 'id'
    | 'company_id'
    | 'company_name'
    | 'impersonated'
    | 'impersonated_by'
    | 'impersonation_expires_at'
    | 'timezone'
  > {
    const timezone =
      (user['timezone'] != null ? String(user['timezone']).trim() : '') ||
      extras?.timezone?.trim() ||
      undefined;

    return {
      token,
      id: user.id != null ? String(user.id) : undefined,
      company_id: extras?.company_id,
      company_name: extras?.company_name,
      impersonated: true,
      impersonated_by: {
        email: imp.actor_email != null ? String(imp.actor_email) : undefined,
        id: imp.actor_id != null ? String(imp.actor_id) : undefined,
        name: imp.actor_name != null ? String(imp.actor_name) : undefined,
      },
      impersonation_expires_at: imp.expires_at != null ? String(imp.expires_at) : undefined,
      timezone,
    };
  }

  startImpersonation(
    payload: ImpersonationSessionPayload,
    options?: { openInNewTab?: boolean }
  ): void {
    const openInNewTab = options?.openInNewTab !== false;
    const rawTarget = String(payload.target_type || payload.role || 'admin');
    if (rawTarget === 'tunnel') {
      const tunnelData = this.buildTunnelSession(payload);
      const route = '/tunnel';
      if (openInNewTab && this.openSessionInNewTab('tunnel', tunnelData, route)) {
        return;
      }
      this.applyTunnelSessionInCurrentTab(tunnelData);
      return;
    }

    const role =
      rawTarget === 'staff' || payload.role === UserRole.STAFF
        ? UserRole.STAFF
        : rawTarget === 'attendant_plus' || payload.role === UserRole.ATTENDANT_PLUS
          ? UserRole.ATTENDANT_PLUS
          : rawTarget === 'attendant' || payload.role === UserRole.ATTENDANT
            ? UserRole.ATTENDANT
            : UserRole.ADMIN;
    const userData = this.buildUserSession(payload, role);
    const route = this.roleService.getDefaultRoute(role);

    if (openInNewTab && this.openSessionInNewTab('user', userData, route)) {
      return;
    }
    this.applyUserSessionInCurrentTab(userData, role);
  }

  exitImpersonation(): void {
    // Clear only this tab's impersonation session — do not wipe shared shop userData/tunnelData.
    clearActiveImpersonationSessions();
    this.authService.user.set(null);
    this.locationService.clearLocationCache();
    this.packageService.clearPackagesCache();
    this.employeeService.clearProfileCache();
    this.clearBanner();

    // Prefer closing the impersonation tab and returning focus to Super Admin.
    if (typeof window !== 'undefined' && window.opener && !window.opener.closed) {
      try {
        window.opener.focus();
      } catch {
        // ignore cross-origin focus issues
      }
      window.close();
    }

    const superAdminRaw = localStorage.getItem('superAdminData');
    if (superAdminRaw) {
      try {
        const superAdmin = JSON.parse(superAdminRaw);
        if (superAdmin?.token) {
          this.roleService.setUserRole(UserRole.SUPER_ADMIN);
          this.logger.info('Impersonation exited — returning to Super Admin', {}, 'Impersonation');
          void this.router.navigateByUrl(getDefaultImpersonationRoute());
          return;
        }
      } catch {
        // fall through
      }
    }

    this.roleService.clearUserRole();
    void this.router.navigate(['/admin/login']);
  }

  private openSessionInNewTab(
    kind: 'user' | 'tunnel',
    session: Record<string, any>,
    targetRoute: string
  ): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    writeImpersonationHandoff({
      kind,
      session,
      targetRoute,
      createdAt: Date.now(),
    });

    // Omit noopener so Exit can focus/close against the Way Superuser opener.
    const opened = window.open(targetRoute, '_blank');
    if (!opened) {
      clearImpersonationHandoff();
      this.logger.warn(
        'Impersonation popup blocked — falling back to same tab',
        {},
        'Impersonation'
      );
      return false;
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Impersonation opened',
      detail: 'Opened in a new tab. This tab stays as Super Admin.',
      life: 5000,
    });
    this.logger.info(
      'Impersonation handoff opened in new tab',
      { targetRoute, kind },
      'Impersonation'
    );
    return true;
  }

  private buildUserSession(
    payload: ImpersonationSessionPayload,
    role: UserRole
  ): Record<string, any> {
    return {
      token: payload.token,
      id: payload.id,
      email: payload.email,
      name: payload.name,
      profileImage: 'assets/images/switch-account/Frame%201171276019.svg',
      location: payload.location_id || payload.location,
      role,
      timezone: payload['timezone'],
      company_id: payload.company_id,
      company_name: payload.company_name,
      impersonated: true,
      impersonated_by: payload.impersonated_by,
      impersonation_expires_at: payload.impersonation_expires_at,
      target_type: payload.target_type || 'admin',
      date: new Date(),
    };
  }

  private buildTunnelSession(payload: ImpersonationSessionPayload): Record<string, any> {
    const locationId = String(payload.location_id || payload.location || payload.id || '').trim();
    const shopId = String(payload.company_id || payload['shop_id'] || '').trim();
    return {
      token: payload.token,
      role: UserRole.TUNNEL,
      locationId,
      location_id: locationId,
      location: locationId,
      shopId: shopId || undefined,
      branch: payload.branch,
      email: payload.email,
      name: payload.name,
      company_id: payload.company_id,
      company_name: payload.company_name,
      timezone: payload['timezone'],
      impersonated: true,
      impersonated_by: payload.impersonated_by,
      impersonation_expires_at: payload.impersonation_expires_at,
      target_type: 'tunnel',
      date: new Date(),
    };
  }

  private applyUserSessionInCurrentTab(userData: Record<string, any>, role: UserRole): void {
    this.locationService.clearLocationCache();
    this.packageService.clearPackagesCache();
    this.employeeService.clearProfileCache();
    removeImpersonationStorageItem(IMPERSONATION_TUNNEL_DATA_KEY);
    setImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY, JSON.stringify(userData));
    this.authService.user.set(userData);
    this.roleService.setUserRole(role);
    this.applyBannerFromSession(userData);
    void this.router.navigate([this.roleService.getDefaultRoute(role)]);
  }

  private applyTunnelSessionInCurrentTab(tunnelData: Record<string, any>): void {
    this.locationService.clearLocationCache();
    this.packageService.clearPackagesCache();
    this.employeeService.clearProfileCache();
    removeImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY);
    this.authService.user.set(null);
    setImpersonationStorageItem(IMPERSONATION_TUNNEL_DATA_KEY, JSON.stringify(tunnelData));
    this.roleService.setUserRole(UserRole.TUNNEL);
    this.applyBannerFromSession(tunnelData);
    void this.router.navigate(['/tunnel']);
  }

  private applyBannerFromSession(session: Record<string, any>): void {
    const name = String(session['name'] || '');
    const email = String(session['email'] || '');
    const targetType = String(session['target_type'] || session['role'] || 'admin');
    const who = name && email ? `${name} (${email})` : name || email || 'user';
    this.bannerTargetLabel.set(`Impersonated: ${who} · ${targetType}`);

    const by = session['impersonated_by'];
    let actorEmail = '';
    if (by && typeof by === 'object') {
      actorEmail = String((by as Record<string, any>)['email'] || '');
    }
    this.bannerActorLabel.set(
      actorEmail ? `Impersonated by: ${actorEmail}` : 'Impersonated by: Super Admin'
    );

    this.bannerExpiresAt.set(this.formatExpiresLabel(session));
    this.isImpersonating.set(true);
  }

  private formatExpiresLabel(session: Record<string, any>): string {
    const expires = String(session['impersonation_expires_at'] || '').trim();
    if (!expires) {
      return '';
    }
    const sessionTz = typeof session['timezone'] === 'string' ? session['timezone'].trim() : '';
    const timeZone =
      sessionTz || getUserTimezone() || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const formatted = formatDateTimeInTimeZone(expires, timeZone);
    if (!formatted) {
      return `Expires ${expires}`;
    }
    return `Expires ${formatted} (${timeZone})`;
  }

  private clearBanner(): void {
    this.isImpersonating.set(false);
    this.bannerTargetLabel.set('');
    this.bannerActorLabel.set('');
    this.bannerExpiresAt.set('');
  }
}
